import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';

// Services
import proposalsServices from '../../../services/proposalsServices';

// Auto-save
import useAutoSave from '../../../hooks/useAutoSave';
import AutoSaveIndicator from '../../AutoSaveIndicator/AutoSaveIndicator';

const ProposalInstallationSection = ({ proposal, id, refresh }) => {
  const [installationPrice, setInstallationPrice] = useState(0);
  const dirtyRef = useRef(false);

  // Auto-save function
  const saveInstallationPrice = useCallback(async (price) => {
    const response = await proposalsServices.update(id, { installationPrice: price });
    dirtyRef.current = false;
    if (response.status === 200 && refresh) {
      refresh();
    }
  }, [id, refresh]);

  // Auto-save on Enter and on blur — no timer (numeric field, a period is
  // the decimal separator so word-boundary saving does not apply here)
  const {
    isSaving,
    hasUnsavedChanges,
    lastSaved,
    error,
    markPending,
    saveNow,
    getLastSavedText,
  } = useAutoSave(saveInstallationPrice);

  const flushPending = useCallback(() => {
    saveNow().catch(() => {
      toast.error('Auto-save failed. Your changes will be retried on the next edit.');
    });
  }, [saveNow]);

  useEffect(() => {
    if (proposal?.installationPrice !== undefined && !dirtyRef.current) {
      setInstallationPrice(Number(proposal.installationPrice) || 0);
    }
  }, [proposal]);

  const handleChange = (e) => {
    const newValue = Number(e.target.value) || 0;
    dirtyRef.current = true;
    setInstallationPrice(newValue);
    markPending(newValue);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Installation Price</Typography>
        <AutoSaveIndicator
          isSaving={isSaving}
          hasUnsavedChanges={hasUnsavedChanges}
          lastSaved={lastSaved}
          error={error}
          getLastSavedText={getLastSavedText}
          variant="chip"
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel>Installation Price</InputLabel>
          <OutlinedInput
            type="number"
            value={installationPrice || ''}
            onChange={handleChange}
            onBlur={flushPending}
            onKeyUp={(e) => { if (e.key === 'Enter') flushPending(); }}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            label="Installation Price"
          />
        </FormControl>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Total labor cost for all systems installation
      </Typography>
    </Box>
  );
};

export default ProposalInstallationSection;
